import { Stack, Typography } from "@mui/material";
import { Fragment, useEffect } from "react";
import { BackButton } from "react-common-ui-library/FPACT/Buttons";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BACK_BUTTON_TEXT,
  ENABLE_ACCOUNT_ROUTE,
  FORGOT_PASSWORD,
  FORGOT_PASSWORD_ROUTE,
  H1,
  H3,
  LOGIN_INFORMATION_ROUTE,
  LOGIN_ROUTE,
  MODE_ALL,
  MY_PROFILE_ACCOUNT_SETTINGS_ROUTE,
  PASSWORD_UPDATE_SUCCESSFUL_TOAST,
  SPACING_2_5__2,
} from "../../../Constants/CommonConstants";
import {
  CURRENT_PASSWORD,
  INVALID_CURRENT_PASSWORD,
  INVALID_CURRENT_PASSWORD_ERROR_MESSAGE,
  INVALID_CURRENT_PASSWORD_TYPE,
  PASSWORD_UPDATED,
  PASSWORD_UPDATED_SUCCESSFULLY,
  RESET_PASSWORD_BACK_BUTTON,
  RESET_PASSWORD_DESCRIPTION,
  RESET_PASSWORD_FORM,
  RESET_PASSWORD_HEADER,
} from "../../../Constants/ResetPasswordConstants";
import {
  SLICE_LOGIN,
  SLICE_MY_PROFILE,
  THUNK_POST_RESET_FORGOT_PASSWORD,
  THUNK_PUT_RESET_MY_PROFILE_PASSWORD,
} from "../../../Constants/SliceAndThunkConstants";
import {
  clearLoginApiResponse,
  postResetForgotPassword,
  postResetPassword,
} from "../../../Features/LoginSlice";
import {
  clearMyProfileApiResponse,
  putResetMyProfilePassword,
} from "../../../Features/MyProfileSlice";
import { setDisplayToast } from "../../../Features/UtilityDataSlice";
import { manageApiResponseSelector } from "../../../Selectors/ApiResponseSelector";
import { StyledTypographyHeader } from "../../../Styles/Common/CommonStyles";
import { StyledResetPasswordBox } from "../../../Styles/Login/ForgotPassword/ResetPasswordStyles";
import ResetPasswordFormElements from "./ResetPasswordFormElements";
import { getRecentDispatchedThunkName } from "./ResetPasswordUtils";
import { manageLocaleSelector } from "../../../Selectors/LocaleSelector";

/**
 * @description ResetPassword component contains New Password and Confirm Password to reset the password and redirects the user to Login on success
 * @returns {ReactNode} Render the New Password and Confirm Password inputs with action buttons.
 */
const ResetPassword = () => {
  const intl = useIntl();

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const location = useLocation();

  const { language } = useSelector((state) => manageLocaleSelector(state)); 

  const { previousScreen } = location.state;

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(
      state,
      previousScreen === LOGIN_INFORMATION_ROUTE
        ? SLICE_MY_PROFILE
        : SLICE_LOGIN,
      getRecentDispatchedThunkName(previousScreen)
    )
  );

  const { isFailure, isSuccess, description } = apiResponses;

  const methods = useForm({
    mode: MODE_ALL,
    criteriaMode: MODE_ALL,
  });

  const onBackButtonClickHandler = () => {
    switch (previousScreen) {
      case LOGIN_INFORMATION_ROUTE:
        navigate(
          `/${MY_PROFILE_ACCOUNT_SETTINGS_ROUTE}/${LOGIN_INFORMATION_ROUTE}`
        );
        break;
      case FORGOT_PASSWORD_ROUTE:
        navigate(`/${FORGOT_PASSWORD_ROUTE}`);
        break;
      case LOGIN_ROUTE:
        navigate(`/${LOGIN_ROUTE}`);
        break;
      default:
        break;
    }
  };

  const handleResetForgotPassword = (data) => {
    const payload = {
      password: data?.newPassword,
      confirmPassword: data?.confirmPassword,
    };
    dispatch(postResetForgotPassword(payload));
  };

  const handleResetProfilePassword = (data) => {
    const payload = {
      currentPassword: data?.currentPassword,
      newPassword: data?.newPassword,
    };
    dispatch(putResetMyProfilePassword(payload));
  };

  const handleResetPassword = (data) => {
    const payload = {
      password: data?.newPassword,
      confirmPassword: data?.confirmPassword,
    };
    dispatch(postResetPassword(payload));
  };

  const handleOnSubmit = (data) => {
    switch (previousScreen) {
      case FORGOT_PASSWORD:
      case ENABLE_ACCOUNT_ROUTE:
        handleResetForgotPassword(data);
        break;
      case LOGIN_ROUTE:
        handleResetPassword(data);
        break;
      case LOGIN_INFORMATION_ROUTE:
        handleResetProfilePassword(data);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (
      previousScreen === FORGOT_PASSWORD_ROUTE ||
      previousScreen === LOGIN_ROUTE
    ) {
      if (isSuccess && description === PASSWORD_UPDATED) {
        navigate(`/${LOGIN_ROUTE}`);
        dispatch(setDisplayToast(PASSWORD_UPDATE_SUCCESSFUL_TOAST));
      }
    } else {
      if (description === INVALID_CURRENT_PASSWORD) {
        methods?.setError(CURRENT_PASSWORD, {
          type: INVALID_CURRENT_PASSWORD_TYPE,
          message: intl.formatMessage({
            id: INVALID_CURRENT_PASSWORD_ERROR_MESSAGE,
          }),
        });
      }
      if (description === PASSWORD_UPDATED_SUCCESSFULLY) {
        navigate(
          `/${MY_PROFILE_ACCOUNT_SETTINGS_ROUTE}/${LOGIN_INFORMATION_ROUTE}`
        );
        dispatch(setDisplayToast(PASSWORD_UPDATE_SUCCESSFUL_TOAST));
      }
    }
  }, [
    description,
    isFailure,
    isSuccess,
    navigate,
    dispatch,
    previousScreen,
    methods,
    intl,
    language
  ]);

  useEffect(() => {
    return () => {
      if (
        previousScreen === FORGOT_PASSWORD_ROUTE ||
        previousScreen === LOGIN_ROUTE
      ) {
        dispatch(clearLoginApiResponse(THUNK_POST_RESET_FORGOT_PASSWORD));
      } else {
        dispatch(clearMyProfileApiResponse(THUNK_PUT_RESET_MY_PROFILE_PASSWORD));
      }
    }
  }, [dispatch, previousScreen])

  return (
    <Fragment>
      <BackButton
        id={RESET_PASSWORD_BACK_BUTTON}
        backButtonOnClickHandler={onBackButtonClickHandler}
        backButtonText={intl.formatMessage({ id: BACK_BUTTON_TEXT })}
      />
      <StyledResetPasswordBox>
        <Stack spacing={SPACING_2_5__2}>
          <StyledTypographyHeader
            id={RESET_PASSWORD_HEADER}
            component={H1}
            variant={H3}
          >
            {intl.formatMessage({
              id: RESET_PASSWORD_HEADER,
            })}
          </StyledTypographyHeader>
          <Stack spacing={5}>
            <Typography id={RESET_PASSWORD_DESCRIPTION}>
              {intl.formatMessage({
                id: RESET_PASSWORD_DESCRIPTION,
              })}
            </Typography>
            <FormProvider {...methods}>
              <form
                id={RESET_PASSWORD_FORM}
                onSubmit={methods.handleSubmit(handleOnSubmit)}
              >
                <ResetPasswordFormElements handleOnSubmit={handleOnSubmit} />
              </form>
            </FormProvider>
          </Stack>
        </Stack>
      </StyledResetPasswordBox>
    </Fragment>
  );
};

export default ResetPassword;
